import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { EnumToken, CountryToken, CountryRegionToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FormField } from '~/components/system/form-field';
import { applyServerValidation, serverMessage } from '~/lib/api/server-error';
import { useTaxRuleMutations } from '~/hooks/use-tax-rules';
import { taxRuleSchema, EMPTY_TAX_RULE, type TaxRuleFormValues } from '~/lib/schemas/tax-rule';

const NO_REGION = '__none__';

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	initial: TaxRuleFormValues | null;
	statuses: EnumToken[];
	countries: CountryToken[];
	regions: CountryRegionToken[];
}

/** Add / edit a tax rule (Φορολογικός Κανόνας). */
export function TaxRuleEditDialog({ open, onOpenChange, initial, statuses, countries, regions }: Props) {
	const { t } = useTranslation();
	const { create, update } = useTaxRuleMutations();
	const form = useForm<TaxRuleFormValues>({
		resolver: zodResolver(taxRuleSchema) as Resolver<TaxRuleFormValues>,
		defaultValues: initial ?? EMPTY_TAX_RULE,
		mode: 'onChange'
	});
	const { register, handleSubmit, reset, setError, setValue, watch, formState } = form;
	const isEdit = Boolean(initial?.id);

	useEffect(() => {
		if (open) reset(initial ?? EMPTY_TAX_RULE);
	}, [open, initial, reset]);

	const onSubmit = handleSubmit(async values => {
		try {
			if (values.id) await update.mutateAsync(values);
			else await create.mutateAsync(values);
			toast.success(t('common.save'));
			onOpenChange(false);
		} catch (err) {
			if (!applyServerValidation(err, setError)) toast.error(serverMessage(err));
		}
	});

	const countryId = watch('country_id');
	const regionId = watch('country_region_id');
	const taxStatus = watch('tax_status');

	// Regions are scoped to the selected country (CountryRegionToken.cid).
	const countryRegions = useMemo(() => (countryId == null ? [] : regions.filter(r => r.cid === countryId)), [regions, countryId]);

	const onCountryChange = (v: string) => {
		const next = Number(v);
		setValue('country_id', next, { shouldDirty: true, shouldValidate: true });
		// Clear a region that no longer belongs to the newly-selected country.
		if (regionId != null && !regions.some(r => r.id === regionId && r.cid === next)) {
			setValue('country_region_id', null, { shouldDirty: true });
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{isEdit ? t('prices.taxRules.editTitle') : t('prices.taxRules.addTitle')}</DialogTitle>
					<DialogDescription>{t('prices.taxRules.formSubtitle')}</DialogDescription>
				</DialogHeader>
				<form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
					<FormField label={t('prices.taxRules.columns.country')} error={formState.errors.country_id?.message}>
						<Select value={countryId == null ? '' : String(countryId)} onValueChange={onCountryChange}>
							<SelectTrigger
								className="w-full"
								aria-label={t('prices.taxRules.columns.country')}
								aria-invalid={Boolean(formState.errors.country_id)}>
								<SelectValue placeholder={t('prices.taxRules.selectCountry')} />
							</SelectTrigger>
							<SelectContent>
								{countries.map(c => (
									<SelectItem key={c.id} value={String(c.id)}>
										{c.nm}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</FormField>
					<FormField label={t('prices.taxRules.columns.region')}>
						<Select
							value={regionId == null ? NO_REGION : String(regionId)}
							onValueChange={v => setValue('country_region_id', v === NO_REGION ? null : Number(v), { shouldDirty: true })}
							disabled={countryId == null}>
							<SelectTrigger className="w-full" aria-label={t('prices.taxRules.columns.region')}>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value={NO_REGION}>{t('prices.taxRules.noRegion')}</SelectItem>
								{countryRegions.map(r => (
									<SelectItem key={r.id} value={String(r.id)}>
										{r.nm}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</FormField>
					<div className="grid grid-cols-2 gap-4">
						<FormField label={t('prices.taxRules.columns.postalCode')} error={formState.errors.postal_code?.message}>
							<Input {...register('postal_code')} maxLength={20} aria-invalid={Boolean(formState.errors.postal_code)} />
						</FormField>
						<FormField label={t('prices.taxRules.columns.taxStatus')} error={formState.errors.tax_status?.message}>
							<Select value={String(taxStatus)} onValueChange={v => setValue('tax_status', Number(v), { shouldDirty: true })}>
								<SelectTrigger className="w-full" aria-label={t('prices.taxRules.columns.taxStatus')}>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{statuses.map(o => (
										<SelectItem key={o.id} value={String(o.id)}>
											{o.nm}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</FormField>
					</div>
					<DialogFooter>
						<Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
							{t('common.cancel')}
						</Button>
						<Button type="submit" disabled={create.isPending || update.isPending}>
							{create.isPending || update.isPending ? t('common.saving') : t('common.save')}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
